/**
 * 家长端 · 积分流水（见契约 1.3「积分流水」「手动调分」「纠错冲正」）。
 *
 * 这一页要做成"能解释清楚每一分怎么来的"：
 *  - 顶部是余额 + 累计获得 / 消费 + 本周本月变化；
 *  - 中部是按原因筛选的逐笔明细（时间、原因、正负、备注、余额快照）；
 *  - 底部两块写操作：**手动奖励 / 扣分**（理由必填，会进审计）与
 *    **纠错冲正**（对某笔流水生成反向流水，明确告知"原记录不会被修改"）。
 *
 * 家长点评：挂在这一页下方，因为"只有分没有话，孩子会把它理解成冷冰冰的交易"
 * （方案 3.4.4），所以点评必须在家长看得见分的地方顺手可写。
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { POINTS_REASONS, normalizePointsDelta, type PointsReason } from '@fht/shared';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { ErrorState, NotReadyState, StatTile } from '../../components/ui/Bits';
import { reasonIcon, reasonLabel } from '../../components/PointsLedgerList';
import { CommentBox } from '../../components/CommentBox';
import {
  useLedger,
  usePointsMutations,
  usePointsSummary,
} from '../../lib/domainQueries';
import { useStudent } from '../../lib/queries';
import { useParentActor } from '../../lib/ActorContext';
import { canAdjust } from '../../lib/uiMode';
import { newIdempotencyKey } from '../../lib/api/client';
import { toErrorMessage } from '../../lib/api/error';
import {
  LEDGER_AMOUNT_TONE_CLASS,
  earnedTotalText,
  ledgerAmountText,
  ledgerAmountTone,
  pointsBreakdownText,
  spentTotalText,
  weekMonthChangeText,
} from '../../lib/pointsDisplay';
import { cn, formatDateTimeFull } from '../../lib/utils';
import type { LedgerEntry } from '../../lib/api/points';

const PAGE_SIZE = 20;

export function ParentStudentPointsPage() {  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const studentQuery = useStudent(id);
  const actor = useParentActor(studentQuery.data?.permission ?? null);
  const canWrite = canAdjust(actor);

  const [page, setPage] = useState(1);
  const [reasonFilter, setReasonFilter] = useState<PointsReason | 'all'>('all');

  const summaryQuery = usePointsSummary(id);
  const ledgerQuery = useLedger(id, page, reasonFilter);
  const mutations = usePointsMutations(id);

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustDelta, setAdjustDelta] = useState('5');
  const [adjustReason, setAdjustReason] = useState<'bonus' | 'penalty' | 'adjust'>('bonus');
  const [adjustNote, setAdjustNote] = useState('');
  const [adjustError, setAdjustError] = useState<string | null>(null);
  const [adjustKey, setAdjustKey] = useState('');

  const [reversing, setReversing] = useState<LedgerEntry | null>(null);
  const [reverseNote, setReverseNote] = useState('');
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [reverseKey, setReverseKey] = useState('');

  /**
   * 这笔**实际会入账**的分值：符号由「类型」决定，不由输入框里的负号决定。
   *
   * 2026-10-18 的 bug 就出在这里：家长点「扣分」（预填 `-5`）、把分值改成 `10`，
   * 旧代码把 `10` 原样发给服务端 → 库里是 `delta=+10, reason='penalty'`
   * （"扣分"却加了 10 分，绿色）。现在「扣分」恒为负、「额外奖励」恒为正，
   * 输入框只填**大小**即可，预览行会告诉家长最终记成多少。
   */
  const adjustMagnitude = useMemo(() => {
    const n = Number(adjustDelta);
    return Number.isFinite(n) ? Math.round(n) : 0;
  }, [adjustDelta]);
  const adjustSignedDelta = useMemo(
    () => normalizePointsDelta(adjustReason, adjustMagnitude),
    [adjustReason, adjustMagnitude],
  );

  const summary = summaryQuery.data;
  const entries = ledgerQuery.data?.entries ?? [];
  const total = ledgerQuery.data?.total ?? 0;
  const notReady = ledgerQuery.data?.notReady ?? false;
  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /**
   * 口径核对行的左边：账实一致时就是页面上那个「当前余额」；
   * 万一账实不符（`balanceConsistent === false`），退回流水的合计，
   * 让这行等式在数学上依然成立 —— 不去粉饰一个"看起来对"的数字。
   */
  const breakdownText = useMemo(
    () =>
      summary && !summary.notReady
        ? pointsBreakdownText({
            balance: summary.balanceConsistent ? summary.balance : summary.ledgerSum,
            earned: summary.totalEarned,
            spent: summary.totalSpent,
            penalty: summary.penaltyTotal,
            adjustment: summary.adjustmentTotal,
          })
        : null,
    [summary],
  );
  const weekStartLabel = summary?.weekStartDate?.slice(5) ?? '周一';
  const monthStartLabel = summary?.monthStartDate?.slice(5) ?? '1 号';

  /** 已被冲正过的原流水 id 集合 —— 这些条目的冲正按钮要禁用。 */
  const reversedIds = useMemo(() => {
    const set = new Set<string>();
    for (const entry of entries) {
      if (entry.reversed) set.add(entry.id);
      if (entry.reversesLedgerId) set.add(entry.reversesLedgerId);
    }
    return set;
  }, [entries]);

  const openAdjust = () => {
    setAdjustOpen(true);
    setAdjustError(null);
    setAdjustNote('');
    // 一次提交一个幂等键：点两次"保存"不会产生两笔积分
    setAdjustKey(newIdempotencyKey('points-adjust'));
  };

  const submitAdjust = async () => {
    // 用归一化后的分值：扣分恒为负（选「扣分」时输入框里的正数会被自动取负）
    const delta = adjustSignedDelta;
    if (!Number.isFinite(delta) || delta === 0) {
      setAdjustError('积分变动不能为 0');
      return;
    }
    if (!adjustNote.trim()) {
      setAdjustError('请填写变动原因（会记入审计，孩子也能看到）');
      return;
    }
    setAdjustError(null);
    try {
      await mutations.adjust.mutateAsync({
        delta,
        reason: adjustReason,
        note: adjustNote.trim(),
        idempotencyKey: adjustKey,
      });
      toast.showToast('已记录这笔调整', 'success');
      setAdjustOpen(false);
    } catch (err) {
      setAdjustError(toErrorMessage(err));
    }
  };

  const openReverse = (entry: LedgerEntry) => {
    setReversing(entry);
    setReverseNote('');
    setReverseError(null);
    setReverseKey(newIdempotencyKey(`points-reverse:${entry.id}`));
  };

  const submitReverse = async () => {
    if (!reversing) return;
    try {
      await mutations.reverse.mutateAsync({
        ledgerId: reversing.id,
        note: reverseNote.trim(),
        idempotencyKey: reverseKey,
      });
      toast.showToast('已生成反向流水', 'success');
      setReversing(null);
    } catch (err) {
      setReverseError(toErrorMessage(err));
    }
  };

  return (
    <AppShell
      kind="parent"
      title={studentQuery.data ? `${studentQuery.data.nickname} 的积分` : '积分明细'}
    >
      <div className="space-y-6">
        {/* ---------- 余额与摘要 ---------- */}
        {summaryQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              label="当前余额"
              value={summary?.notReady ? '—' : (summary?.balance ?? 0)}
              tone="amber"
              hint={summary?.notReady ? '积分接口尚未就绪' : undefined}
            />
            <StatTile label="累计获得" value={earnedTotalText(summary?.totalEarned)} tone="emerald" />
            <StatTile label="累计消费" value={spentTotalText(summary?.totalSpent)} tone="slate" />
            <StatTile
              label="本周 / 本月变化"
              value={weekMonthChangeText(summary?.weekChange, summary?.monthChange)}
            />
          </div>
        )}

        {/*
          口径说明 —— 这一块不是装饰，是"四个数为什么加得起来"的答案。
          2026-10-18 用户看到「获得 470 / 消费 70 / 余额 400」时第一反应是"算错了"：
          其实那 70 里有 60 是**冲正**（纠错），一分钱都没花出去。
          把「扣分」和「调账·冲正」单列，恒等式当场成立，不需要把数字硬凑成好看的样子。
        */}
        {summary && !summary.notReady && (
          <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
            <p>
              口径：<strong>余额 = 累计获得 − 累计消费 − 扣分 + 调账·冲正</strong>
              {breakdownText && (
                <>
                  {' '}
                  · 本页 <span className="font-medium tabular-nums">{breakdownText}</span>
                </>
              )}
              。「扣分」与「调账 / 冲正」单列，<strong>都不计入</strong>累计获得或累计消费。
            </p>
            <p className="mt-1">
              「本周 / 本月变化」是<strong>含冲正</strong>的净变化，按家庭时区{' '}
              {summary.timezone ?? 'Asia/Shanghai'} 划分（本周从 {weekStartLabel} 起、本月从{' '}
              {monthStartLabel} 起）。
            </p>
            {!summary.balanceConsistent && (
              <p className="mt-1 font-medium text-rose-600">
                注意：账本合计 {summary.ledgerSum ?? '—'} 与余额 {summary.balance} 不一致，
                已交给每日校验任务告警（不会自动改数字）。
              </p>
            )}
          </div>
        )}

        {summary && summary.balance < 0 && (
          <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            孩子当前积分透支了。这通常来自「先兑换、后被冲正」，账本依然自洽
            （余额 = 所有流水的合计）。孩子端会提示「下次打卡先补回来」。
          </p>
        )}

        {/* ---------- 写操作入口 ---------- */}
        {canWrite && (
          <Card>
            <CardHeader
              title="手动调整积分"
              description="奖励或扣分都要写理由 —— 孩子能看懂每一分是怎么来的，才会认这套规则。"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  setAdjustReason('bonus');
                  setAdjustDelta('5');
                  openAdjust();
                }}
              >
                🌟 额外奖励
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setAdjustReason('penalty');
                  // 只填大小：扣分的负号由「类型」决定，家长不用自己打 `-`
                  setAdjustDelta('5');
                  openAdjust();
                }}
              >
                ⚠️ 扣分
              </Button>
              <Button variant="secondary" onClick={() => void ledgerQuery.refetch()}>
                刷新流水
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              低龄孩子建议弱化扣分：扣分带来的挫败感容易让孩子放弃整套积分制。
            </p>
          </Card>
        )}

        {/* ---------- 筛选 ---------- */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setReasonFilter('all');
              setPage(1);
            }}
            aria-pressed={reasonFilter === 'all'}
            className={cn(
              'rounded-xl px-3 py-1.5 text-sm font-medium',
              reasonFilter === 'all' ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100',
            )}
          >
            全部
          </button>
          {POINTS_REASONS.map((reason) => (
            <button
              key={reason}
              type="button"
              onClick={() => {
                setReasonFilter(reason);
                setPage(1);
              }}
              aria-pressed={reasonFilter === reason}
              className={cn(
                'rounded-xl px-3 py-1.5 text-sm font-medium',
                reasonFilter === reason ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100',
              )}
            >
              {reasonIcon(reason)} {reasonLabel(reason)}
            </button>
          ))}
        </div>

        {/* ---------- 流水明细 ---------- */}
        <Card>
          <CardHeader
            title="积分流水"
            description="账本只追加、不可修改；需要纠错时用「冲正」生成一笔反向记录。"
          />

          {ledgerQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : ledgerQuery.isError ? (
            <ErrorState message={toErrorMessage(ledgerQuery.error)} onRetry={() => void ledgerQuery.refetch()} />
          ) : notReady ? (
            <NotReadyState feature="积分流水" />
          ) : entries.length === 0 ? (
            <EmptyState
              icon="🧾"
              title="这个筛选下还没有记录"
              description={
                reasonFilter === 'all'
                  ? '孩子完成打卡、或你做一次手动调整之后，明细会出现在这里。'
                  : '换一个筛选条件看看，或者选「全部」。'
              }
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {entries.map((entry) => {
                const amountTone = ledgerAmountTone(entry.delta);
                const reversed = reversedIds.has(entry.id) || entry.reversed === true;
                const isReversal = entry.reversesLedgerId != null;
                return (
                  <li key={entry.id} className="flex flex-wrap items-start gap-3 py-3">
                    <span aria-hidden="true" className="text-xl">
                      {reasonIcon(entry.reason)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-900">{reasonLabel(entry.reason)}</p>
                        {isReversal && <Badge tone="warn">冲正记录</Badge>}
                        {reversed && !isReversal && <Badge tone="neutral">已被冲正</Badge>}
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {entry.note || '—'} · {formatDateTimeFull(entry.createdAt)}
                        {entry.balanceAfter !== undefined && ` · 变动后余额 ${entry.balanceAfter}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span
                        className={cn(
                          'font-semibold tabular-nums',
                          LEDGER_AMOUNT_TONE_CLASS[amountTone],
                        )}
                      >
                        {ledgerAmountText(entry.delta)}
                      </span>
                      {canWrite && !isReversal && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={reversed}
                          title={reversed ? '这笔已经被冲正过了' : '生成一笔反向流水'}
                          onClick={() => openReverse(entry)}
                        >
                          冲正
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {maxPage > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                上一页
              </Button>
              <span className="text-sm text-slate-500">
                第 {page} / {maxPage} 页 · 共 {total} 条
              </span>
              <Button variant="secondary" size="sm" disabled={page >= maxPage} onClick={() => setPage((p) => p + 1)}>
                下一页
              </Button>
            </div>
          )}
        </Card>

        {/* ---------- 点评（积分的配套，见方案 3.4.4） ---------- */}
        <CommentBox
          targetType="period"
          targetId={id}
          title="给孩子写句点评"
          description="只有「分」没有「话」，孩子会把它理解成冷冰冰的交易。写完孩子端立刻能看到。"
          studentId={id}
        />

        <Card>
          <CardHeader title="为什么不能直接改一笔流水？" />
          <p className="text-sm text-slate-500">
            流水表是「事实」的记录。如果允许直接改，孩子问「为什么我的分少了」时就无法追溯。
            所以纠错一律通过<strong>新增一笔反向流水</strong>完成 —— 原记录保留，新记录说明原因，
            两步都能在孩子的界面上看到。
          </p>
        </Card>
      </div>

      {/* ---------- 手动调分 ---------- */}
      <Modal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        title={adjustReason === 'penalty' ? '扣分' : adjustReason === 'bonus' ? '额外奖励' : '手动调整'}
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => setAdjustOpen(false)}>
              取消
            </Button>
            <Button
              variant={adjustReason === 'penalty' ? 'danger' : 'primary'}
              loading={mutations.adjust.isPending}
              onClick={() => void submitAdjust()}
            >
              确认
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Select
            label="类型"
            value={adjustReason}
            onChange={(e) => setAdjustReason(e.target.value as 'bonus' | 'penalty' | 'adjust')}
          >
            <option value="bonus">额外奖励（加分）</option>
            <option value="penalty">扣分</option>
            <option value="adjust">其他调整</option>
          </Select>
          <Input
            label={adjustReason === 'penalty' ? '扣多少分' : adjustReason === 'bonus' ? '加多少分' : '分值'}
            type="number"
            value={adjustDelta}
            onChange={(e) => setAdjustDelta(e.target.value)}
            hint={
              adjustReason === 'penalty'
                ? '填正数即可（如 10）—— 「扣分」会自动记成 -10，不用自己打负号'
                : adjustReason === 'bonus'
                  ? '填正数（如 5）—— 「额外奖励」一律记为正数'
                  : '这个类型不改符号：加分填正数、扣分填负数（如 -5）'
            }
          />
          {/* 预览：让家长在点确认前就看到"最终会记成多少分"，而不是自己去猜符号 */}
          {adjustSignedDelta !== 0 && (
            <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
              这笔会记为{' '}
              <strong
                className={
                  adjustSignedDelta < 0 ? 'font-semibold text-rose-600' : 'font-semibold text-emerald-600'
                }
              >
                {adjustSignedDelta > 0 ? '+' : ''}
                {adjustSignedDelta} 分
              </strong>
              {adjustReason !== 'adjust' && '（符号由「类型」决定）'}
            </p>
          )}
          <Input
            label="原因（必填）"
            value={adjustNote}
            maxLength={200}
            onChange={(e) => setAdjustNote(e.target.value)}
            placeholder="例如：今天主动帮忙做家务"
            hint="这条理由会进入审计日志，孩子也能在自己的积分明细里看到"
          />
          {adjustError && (
            <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {adjustError}
            </p>
          )}
        </div>
      </Modal>

      {/* ---------- 纠错冲正 ---------- */}
      <Modal
        open={reversing !== null}
        onClose={() => setReversing(null)}
        title="纠错冲正"
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => setReversing(null)}>
              取消
            </Button>
            <Button loading={mutations.reverse.isPending} onClick={() => void submitReverse()}>
              生成反向流水
            </Button>
          </div>
        }
      >
        {reversing && (
          <div className="space-y-3">
            <div className="rounded-xl bg-slate-50 p-3 text-sm">
              <p className="text-slate-500">原记录</p>
              <p className="mt-1 font-medium text-slate-800">
                {reasonLabel(reversing.reason)} {reversing.delta > 0 ? '+' : ''}
                {reversing.delta} 分
              </p>
              <p className="text-xs text-slate-500">
                {reversing.note || '—'} · {formatDateTimeFull(reversing.createdAt)}
              </p>
            </div>

            <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
              ⚠️ 原记录<strong>不会被修改或删除</strong>。系统会新增一笔
              <strong>
                {reversing.delta > 0 ? ' -' : ' +'}
                {Math.abs(reversing.delta)} 分
              </strong>
              的反向流水，两笔都保留在孩子的明细里。
            </p>

            <Input
              label="冲正原因（必填）"
              value={reverseNote}
              maxLength={200}
              onChange={(e) => setReverseNote(e.target.value)}
              placeholder="例如：上次奖励金额填错了"
              hint="这条理由会进入审计日志，孩子也能在自己的积分明细里看到"
            />
            {reverseError && (
              <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {reverseError}
              </p>
            )}
          </div>
        )}
      </Modal>
    </AppShell>
  );
}
